import { Menu as BaseMenu } from '@base-ui/react/menu'

import { cn } from '@gto/ui/cn'
import { Radio } from '@gto/ui/radio'

import { menuItemVariants } from './item.styles'

type RadioItemProps<TValue extends string> = Omit<BaseMenu.RadioItem.Props, 'render' | 'value'> & {
  value: TValue
  /**
   * Drop the leading `Radio` dot. The row's own checked tint (`data-[checked]`
   * → `primary-main`, see `menuItemVariants`) already states the selection, so
   * in a short single-select list of terse labels the dot is a second marker
   * for the same fact and reads as form furniture. Semantics are untouched —
   * Base UI still renders `menuitemradio` + `aria-checked`.
   */
  hideRadio?: boolean
}

/**
 * Menu row that participates in a `Menu.RadioGroup`. Base UI owns the
 * `data-checked` state and selection lifecycle; the leading visual is the
 * shared `Radio` (no separate indicator needed). The Radio is purely
 * decorative — pointer events are disabled so clicks land on the row.
 *
 * Generic over the option value so it stays in lock-step with the typed
 * `RadioGroup` value instead of base-ui's `any`.
 */
export const RadioItem = <TValue extends string>({
  children,
  className,
  hideRadio = false,
  ...props
}: RadioItemProps<TValue>) => {
  return (
    <BaseMenu.RadioItem
      className={cn(
        menuItemVariants(),
        // A controlled `Radio` wraps itself in a block `RadioGroup` div, and an
        // inline-level 16px radio inside a block box sits on the text baseline —
        // a 24px box with the circle pinned to its top. The row then centres the
        // 24px wrapper, leaving the circle 3.75px high (visible on unchecked
        // rows, where there is no filled dot to mask it). Collapsing the wrapper
        // to its content height puts the circle back on the row's centre line.
        '[&>[role=radiogroup]]:flex',
        className,
      )}
      {...props}
      render={(renderProps, state) => (
        <div {...renderProps}>
          {!hideRadio && (
            <Radio
              checked={state.checked}
              className="pointer-events-none"
              size="small"
              tabIndex={-1}
            />
          )}
          {children}
        </div>
      )}
    />
  )
}
